import { forwardRef, useCallback, useImperativeHandle, useState } from "react";

import { FormProvider, useForm } from "react-hook-form";

import Box from "@/components/Box";
import AddressFinderSearch from "@/components/shared/AddressFinderSearch";
import { shippingAddressDefaultsFromGraphQL } from "@/utils/shippingAddressDefaults";
import { handleServerSideErrors, NON_FIELD_ERROR_KEY } from "@/hooks/form/useDjangoGraphqlForm";
import useScrollToError from "@/hooks/form/useScrollToError";
import useShippingCountries from "@/hooks/useShippingCountries";
import ControlledFormField from "@/tpo/ControlledFormField";
import ControlledRadioGroup from "@/tpo/ControlledRadioGroup";
import DateInput from "@/tpo/DateInput";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import FormControl from "@/tpo/FormControl";
import NativeSelect from "@/tpo/NativeSelect";
import Stack from "@/tpo/Stack";
import { get } from "@/utils/nodash";
import { getShippingAddressFieldNames } from "@/utils/idealPostcodesAddress";

function shippingAddressFromBasket(basket) {
  const address = basket?.shippingAddress;
  if (!address) {
    return null;
  }

  return shippingAddressDefaultsFromGraphQL(address);
}

function getReadOnlyProfileFields(patientProfile) {
  const fields = [];

  if (patientProfile?.user?.firstName) {
    fields.push("firstName");
  }
  if (patientProfile?.user?.lastName) {
    fields.push("lastName");
  }
  if (patientProfile?.dateOfBirth) {
    fields.push("dateOfBirth");
  }
  if (patientProfile?.gender) {
    fields.push("gender");
  }

  return fields;
}

function initializePatientPayOrderDetails({ order, patientProfile, basket }) {
  const basketAddress = shippingAddressFromBasket(basket) ?? shippingAddressDefaultsFromGraphQL();

  return {
    orderId: order.id,
    firstName: patientProfile?.user?.firstName ?? "",
    lastName: patientProfile?.user?.lastName ?? "",
    email: patientProfile?.user?.email ?? "",
    dateOfBirth: patientProfile?.dateOfBirth || "",
    gender: patientProfile?.gender ?? "",
    ...basketAddress
  };
}

const PatientPayOrderDetailsForm = forwardRef(function PatientPayOrderDetailsForm(
  { authToken, order, patientProfile, basket, readOnly = false },
  ref
) {
  const [scrollToError, setScrollToError] = useState(0);
  const shippingCountries = useShippingCountries();
  const readOnlyProfileFields = getReadOnlyProfileFields(patientProfile);

  const methods = useForm({
    defaultValues: initializePatientPayOrderDetails({ order, patientProfile, basket })
  });

  const applyServerErrors = useCallback(
    errors => {
      handleServerSideErrors(errors, () => setScrollToError(value => value + 1), methods.setError);
    },
    [methods.setError]
  );

  useScrollToError({ scrollToError });

  useImperativeHandle(
    ref,
    () => ({
      applyServerErrors,
      getProceedInput: () => {
        const { id: _addressId, ...input } = methods.getValues();

        return {
          ...input,
          orderId: order.id,
          authToken,
          dateOfBirth: input.dateOfBirth === "--" ? "" : input.dateOfBirth
        };
      }
    }),
    [applyServerErrors, authToken, methods, order.id]
  );

  const nonFieldError = get(methods.formState.errors, NON_FIELD_ERROR_KEY)?.message;
  const hasGuardian = !!patientProfile?.guardian;

  return (
    <FormProvider {...methods}>
      <Stack gap={40}>
        <Stack gap={16}>
          <Box fontFamily="gilroyBold" fontSize={[24, 24, 28]}>
            Patient details
          </Box>
          <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
            Check and complete the below details before proceeding to payment. Contact your
            practitioner if any details are incorrect.
          </Box>
        </Stack>

        {nonFieldError && <Box color="red">{nonFieldError}</Box>}

        <Stack gap={20}>
          <ControlledFormField
            name="firstName"
            label="First name"
            Component={FloatingLabelInput}
            editable={!readOnly && !readOnlyProfileFields.includes("firstName")}
            showPencilIcon={!readOnly && !readOnlyProfileFields.includes("firstName")}
          />
          <ControlledFormField
            name="lastName"
            label="Last name"
            Component={FloatingLabelInput}
            editable={!readOnly && !readOnlyProfileFields.includes("lastName")}
            showPencilIcon={!readOnly && !readOnlyProfileFields.includes("lastName")}
          />
          {!hasGuardian && (
            <ControlledFormField
              name="email"
              label="Email"
              Component={FloatingLabelInput}
              editable={false}
              showPencilIcon={false}
            />
          )}
          <FormControl
            label={
              <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="36px">
                Date of birth
              </Box>
            }
          >
            <ControlledFormField
              name="dateOfBirth"
              Component={DateInput}
              getDay={dateStr => dateStr.split("-")?.[2] || ""}
              getMonth={dateStr => dateStr.split("-")?.[1] || ""}
              getYear={dateStr => dateStr.split("-")?.[0] || ""}
              constructDate={({ day, month, year }) => `${year}-${month}-${day}`}
              disabled={readOnly || readOnlyProfileFields.includes("dateOfBirth")}
            />
          </FormControl>
          <FormControl
            label={
              <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="36px">
                Gender
              </Box>
            }
          >
            <ControlledRadioGroup
              Container={Stack}
              containerProps={{ gap: 20 }}
              name="gender"
              disabled={readOnly || readOnlyProfileFields.includes("gender")}
              values={[
                { label: "Male", value: "Male" },
                { label: "Female", value: "Female" }
              ]}
            />
          </FormControl>
        </Stack>

        {hasGuardian && (
          <Stack gap={20}>
            <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="36px">
              Guardian details
            </Box>
            <FloatingLabelInput
              name="guardianFirstName"
              label="Guardian first name"
              value={patientProfile.guardian.user?.firstName ?? ""}
              onChange={() => {}}
              editable={false}
              showPencilIcon={false}
            />
            <FloatingLabelInput
              name="guardianLastName"
              label="Guardian last name"
              value={patientProfile.guardian.user?.lastName ?? ""}
              onChange={() => {}}
              editable={false}
              showPencilIcon={false}
            />
            <FloatingLabelInput
              name="guardianEmail"
              label="Guardian email"
              value={patientProfile.guardian.user?.email ?? ""}
              onChange={() => {}}
              editable={false}
              showPencilIcon={false}
            />
          </Stack>
        )}

        <Stack gap={20}>
          <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="36px">
            Shipping address
          </Box>
          {!readOnly && (
            <AddressFinderSearch
              disabled={readOnly}
              shippingCountries={shippingCountries}
              fieldNames={getShippingAddressFieldNames()}
              countryValueType="id"
              updateCountry={false}
            />
          )}
          <ControlledFormField
            name="name"
            label="Name"
            Component={FloatingLabelInput}
            editable={!readOnly}
            showPencilIcon={!readOnly}
          />
          <ControlledFormField
            name="line1"
            label="Address line 1"
            Component={FloatingLabelInput}
            editable={!readOnly}
            showPencilIcon={!readOnly}
          />
          <ControlledFormField
            name="line2"
            label="Address line 2"
            Component={FloatingLabelInput}
            editable={!readOnly}
            showPencilIcon={!readOnly}
          />
          <ControlledFormField
            name="city"
            label="City"
            Component={FloatingLabelInput}
            editable={!readOnly}
            showPencilIcon={!readOnly}
          />
          <ControlledFormField
            name="postcode"
            label="Postcode"
            Component={FloatingLabelInput}
            editable={!readOnly}
            showPencilIcon={!readOnly}
          />
          <ControlledFormField
            name="phoneNumber"
            label="Phone number"
            Component={FloatingLabelInput}
            editable={!readOnly}
            showPencilIcon={!readOnly}
          />
          <FormControl
            label={
              <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="36px">
                Country
              </Box>
            }
            helpText={
              <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} color="midGrey">
                Your practitioner selected the shipping country when placing this order. It cannot
                be changed here.
              </Box>
            }
            spaceAboveChildren={4}
          >
            <ControlledFormField name="country" Component={NativeSelect} label="" readOnly={true}>
              <option value="">Select country</option>
              {shippingCountries.map((country, idx) => (
                <option key={`${country.isoCode}-${idx}`} value={country.id}>
                  {country.name}
                </option>
              ))}
            </ControlledFormField>
          </FormControl>
        </Stack>
      </Stack>
    </FormProvider>
  );
});

export default PatientPayOrderDetailsForm;
